import { EXCEPTION_TYPE } from 'src/constants/ELDConstants';
import { HosLog } from 'src/features/hosLogs/types';
import { GeoLocationDataType } from '../features/hosLogs/hosLog/types';

export interface FetchLogsPayload {
  startDate: string;
  endDate: string;
  employeeIds: number;
  groupIds?: number[];
  startIndex?: number;
  count?: number;
  includeExemptLogs?: boolean;
}

export interface FetchLogPayload {
  logId: string;
}

export class ResponseListData {
  indexOffset = '0';
  paginatedResultCount = 0;
  results: Array<HosLog> = [];
  totalResultCount = 0;
}

export interface DataModificationPayload {
  data: Record<string, unknown>;
}

export interface DutyStatus {
  dutyStatusCode: number;
  dutyStatusName: string;
}

export interface FetchDutyStatusPayload {
  userId: number;
}

export interface ChangeDutyStatusPayload {
  employeeId: number;
  dutyStatusName: string;
  timestamp: string;
  comment: string;
  location: string;
  latitude: number;
  longitude: number;
  enableReverseGeocode: boolean;
}

export interface CertifyHosLogPayload {
  logDate: string;
  logId: number;
}

export interface MultiplyCertifyHosLogs {
  employeeId: number;
  logDate: string;
  updateDay: boolean;
  logIds: MultipleLogIdsPayload;
}

export interface MultipleLogIdsPayload {
  logIds: string[];
}

export interface PendingException {
  exceptionId?: string;
  employeeId: number;
  logDate: string;
  exceptionTimestamp: string;
  exceptionType: EXCEPTION_TYPE;
  comment: string;
  location: string;
}

export interface SaveStatusPayload {
  employeeId: number;
  logDate: string;
  statusPayload: {
    dutyStatusName: string;
    timestamp: string;
    comment: string;
    location: string;
    geoLocationData?: GeoLocationDataType | null;
    duration?: string | number;
    latitude?: number;
    longitude?: number;
    enableReverseGeocode?: boolean;
    odometer: number | null;
    editedOdometer: number | null;
  };
  modifiedOriginLogPayload?: SaveStatusPayload;
  previousEventPayload?: SaveStatusPayload;
  nextPeriodPayload?: SaveStatusPayload;
}

export interface SaveExceptionPayload {
  employeeId: number;
  exceptionDate: string;
}

export interface ExportAllHosLogsPayload {
  startDate: string;
  endDate: string;
  emails: string[];
  employeeIds: number[];
  deliveryFormat: string;
  includeViolations: boolean;
  requesterId: number;
}

export interface FetchHosExportLinkPayload {
  jobId: string;
}
export interface FetchHosExportLinkResponse {
  reportUrl: string | null;
}

export interface FetchMovementsPayload {
  powerUnitNumber: string;
  companyId: number;
  status: AssignmentStatus;
  startTimestamp: string;
  endTimestamp: string;
  offset?: number;
  limit?: number;
}

export interface FetchMovementAssignmentsPayload {
  startTimestamp: string;
  endTimestamp: string;
  employeeId: number;
  assignmentStatus: AssignmentStatus;
}

export interface CreateMovementPayload {
  employeeId: number;
  movements: number[];
}

export interface UpdateMovementPayload {
  employeeId: number;
  editIds: number[];
}

export interface SaveFormsPayload {
  logDate: string;
  employeeId: number;
  formPayload: {
    fmcsaLogDate: string;
    assetInformation?: {
      powerUnitNumbers?: string[];
      trailerNumbers?: string[];
    };
    shippingNumbers?: string[];
    coDriver?: string;
    additionalNotes?: string;
  };
}

export interface MovementsResponseDto {
  status: string;
  responseBody: {
    results: EldMovement[];
  };
  pagination: EldMovementPagination;
}

export type ClosedEldMovement = EldMovement & {
  endRecordId: string;
  endTimestamp: string;
};

export enum EditOperationType {
  insert = 'insert',
  replace = 'replace',
  deactivate = 'deactivate',
}

export interface EditOperation {
  operationType: EditOperationType;
  eldrId: string;
}

export interface MovementAssignment {
  movementId: number;
  companyId: number;
  powerUnitNumber: string;
  vdaUnitNumber: string;
  assignedEmployeeId: number;
  originallyAssigned: boolean;
  driveType: string;
  currentAssignmentStatus: string;
  startTimestamp: string;
  endTimestamp: string;
  startRecordId: string;
  endRecordId: string;
  startOdometer: number;
  endOdometer: number;
  startLatitude: number;
  startLongitude: number;
  endLatitude: number;
  endLongitude: number;
  startLocationString: string;
  endLocationString: string;
  startCompanyLocation: string | null;
  endCompanyLocation: string | null;
  startEngineHours: number;
  endEngineHours: number;
  specialDriveStatus: string | null;
  elapsedOdometer: number;
  elapsedEngineHours: number;
  elapsedSeconds: number;
  editId: number;
  assignedAs: string;
  assignedBy: number;
  assignedTo: number;
  assignedOn: string;
  assignmentStatus: string;
  editOperations: EditOperation[];
}

export interface ProposedTrip {
  employeeId: number;
  movementId: number;
  movementAssignment: MovementAssignment;
  editOperations: EditOperation[];
  claimed?: boolean;
  rejected?: boolean;
}

export interface MovementAssignmentsResponseDto {
  status: string;
  responseBody: ProposedTrip[];
}

export interface MovementsActionPayload {
  type: AssignmentStatus;
  movements: EldMovement[] | ProposedTrip[];
}

export interface MovementClaimPayload {
  type: AssignmentStatus;
  movementId: number;
}

export enum EldDriveType {
  D = 'D',
  PC = 'PC',
  YM = 'YM',
}
export enum AssignmentStatus {
  UNASSIGNED = 'Unassigned',
  UNASSIGNED_HANDLED = 'UnassignedHandled',
  ASSIGNED = 'Assigned',
  PROPOSED = 'Proposed',
  REJECTED = 'Rejected',
  ACCEPTED = 'Accepted',
  CLAIMED = 'Claimed',
}

export interface ClaimMovementsPayload {
  status: AssignmentStatus;
  movementIds: number[];
}
export interface EldMovement {
  movementId: number;
  companyId: number;
  powerUnitNumber: string;
  vdaUnitNumber?: string;
  assignedEmployeeId?: number | null;
  originallyAssigned: boolean;
  driveType: EldDriveType;
  currentAssignmentStatus: AssignmentStatus;
  startTimestamp: string;
  endTimestamp?: string;
  startRecordId: string;
  endRecordId?: string;
  startOdometer?: number;
  endOdometer?: number;
  startLatitude?: number;
  startLongitude?: number;
  endLatitude?: number;
  endLongitude?: number;
  startLocationString?: string | null;
  endLocationString?: string | null;
  startCompanyLocation?: number;
  endCompanyLocation?: number;
  startEngineHours?: number;
  endEngineHours?: number;
  specialDriveStatus?: string;
  elapsedOdometer?: number | null;
  elapsedEngineHours?: number | null;
  elapsedSeconds?: number | null;
  claimed?: boolean;
  rejected?: boolean;
}

export interface EldMovementPagination {
  total: number;
  hasNextPage: boolean;
  nextPageOffset: number;
}
